import type { Deal, DealPipeline, DealStage } from "../types";

export type FunnelDefinition = {
  key: DealPipeline;
  label: string;
  shortLabel: string;
  stages: DealStage[];
  wonStage: string;
  lostStage: string;
  defaultCurrency: "USD" | "CLP";
};

export const B2B_PILOT_STAGES: DealStage[] = [
  { value: "b2b_new", label: "Nuevo" },
  { value: "b2b_contacted", label: "Contactado" },
  { value: "b2b_demo_scheduled", label: "Demo agendada" },
  { value: "b2b_demo_completed", label: "Demo realizada" },
  { value: "b2b_qualified", label: "Calificado" },
  { value: "b2b_pilot_proposal", label: "Propuesta piloto" },
  { value: "b2b_negotiation", label: "Negociación" },
  { value: "b2b_won", label: "Ganado" },
  { value: "b2b_lost", label: "Perdido" },
];

export const B2B_RECURRING_STAGES: DealStage[] = [
  { value: "b2b_pilot_active", label: "Piloto activo" },
  { value: "b2b_results_review", label: "Revisión de resultados" },
  { value: "b2b_recurring_proposal", label: "Propuesta recurrente" },
  { value: "b2b_recurring_negotiation", label: "Negociación" },
  { value: "b2b_recurring_won", label: "Contrato ganado" },
  { value: "b2b_recurring_lost", label: "Contrato perdido" },
];

export const B2C_STAGES: DealStage[] = [
  { value: "b2c_account_created", label: "Cuenta creada" },
  { value: "b2c_email_verified", label: "Email verificado" },
  { value: "b2c_first_login", label: "Primer ingreso" },
  { value: "b2c_first_trade", label: "Primera operación" },
  { value: "b2c_checkout_started", label: "Checkout iniciado" },
  { value: "b2c_paid", label: "Pagado" },
  { value: "b2c_not_converted", label: "No convertido" },
];

export const FUNNELS: Record<DealPipeline, FunnelDefinition> = {
  b2b_pilot: {
    key: "b2b_pilot",
    label: "B2B · Pilotos",
    shortLabel: "B2B Pilotos",
    stages: B2B_PILOT_STAGES,
    wonStage: "b2b_won",
    lostStage: "b2b_lost",
    defaultCurrency: "USD",
  },
  b2b_recurring: {
    key: "b2b_recurring",
    label: "B2B · Renovación y expansión",
    shortLabel: "B2B Recurrente",
    stages: B2B_RECURRING_STAGES,
    wonStage: "b2b_recurring_won",
    lostStage: "b2b_recurring_lost",
    defaultCurrency: "USD",
  },
  b2c_subscription: {
    key: "b2c_subscription",
    label: "B2C · Gratis a pagado",
    shortLabel: "B2C Suscripción",
    stages: B2C_STAGES,
    wonStage: "b2c_paid",
    lostStage: "b2c_not_converted",
    defaultCurrency: "CLP",
  },
};

export const B2B_SEGMENT_CHOICES = [
  { id: "family-office", name: "Family office" },
  { id: "agf", name: "AGF" },
  { id: "hedge-fund", name: "Hedge fund" },
  { id: "broker", name: "Broker / corredora" },
  { id: "fintech", name: "Fintech / roboadvisor" },
  { id: "wealth-manager", name: "Wealth manager" },
  { id: "other", name: "Otro" },
];

export const B2B_OFFER_CHOICES = [
  { id: "guardian-research", name: "Guardian Research" },
  { id: "guardian-risk-api", name: "Guardian Risk API" },
  { id: "guardian-portfolio", name: "Guardian Portfolio" },
  { id: "white-label", name: "White-label / Co-branding" },
];

export const B2C_PLAN_CHOICES = [
  { id: "demo", name: "Demo · Gratis" },
  { id: "basic", name: "Basic · $9.990 CLP/mes" },
  { id: "premium", name: "Premium · $19.990 CLP/mes" },
];

export const LEAD_SOURCE_CHOICES = [
  { id: "houndtrade-web", name: "Web HoundTrade" },
  { id: "houndtrade-app", name: "Aplicación HoundTrade" },
  { id: "linkedin", name: "LinkedIn" },
  { id: "email", name: "Correo" },
  { id: "referral", name: "Referido" },
  { id: "event", name: "Evento" },
  { id: "partner", name: "Partner" },
  { id: "manual", name: "Manual" },
  { id: "other", name: "Otro" },
];

export const SUBSCRIPTION_STATUS_LABELS: Record<string, string> = {
  free: "Gratis",
  active: "De pago",
  past_due: "Pago atrasado",
  canceled: "Cancelado",
  inactive: "Inactivo",
};

export const getFunnel = (pipeline?: string | null) =>
  FUNNELS[(pipeline as DealPipeline) ?? "b2b_pilot"] ?? FUNNELS.b2b_pilot;

export const getFunnelForDeal = (deal: Partial<Deal>) =>
  getFunnel(deal.pipeline);

export const isWonStage = (stage?: string | null) =>
  Object.values(FUNNELS).some((funnel) => funnel.wonStage === stage);

export const isLostStage = (stage?: string | null) =>
  Object.values(FUNNELS).some((funnel) => funnel.lostStage === stage);

export const isClosedStage = (stage?: string | null) =>
  isWonStage(stage) || isLostStage(stage);

export const getOpportunityType = (pipeline: DealPipeline) =>
  pipeline === "b2c_subscription" ? "b2c" : "b2b";

export const getDefaultStage = (pipeline: DealPipeline) =>
  FUNNELS[pipeline].stages[0]!.value;

